import { Component, lazy } from 'react';
import './lazy-route.css';

// 배포가 올라가면 이미 열려 있던 화면이 들고 있는 청크 파일명(해시가 붙는다)이 서버에서
// 사라진다. 그 상태로 lazy 라우트에 들어가면 동적 import 가 거절되는데, Suspense 위에
// 경계가 없으면 React 가 트리를 통째로 내려 화면이 새까맣게 빈다. 중계방이 유일한 lazy
// 라우트라 "중계방에 들어가면 검은 화면, 새로고침하면 정상" 으로 나타났다.
//
// 새로고침하면 새 index.html 과 새 청크 이름을 받으므로 저절로 낫는다. 그래서 한 번은
// 우리가 대신 새로고침한다. 그래도 안 되면(파일이 아니라 통신 문제였다면) 경계가 메시지를
// 띄운다 - 무한 새로고침도, 검은 화면도 아니다.
const RELOAD_KEY = 'doroparty:route-chunk-reload';
const RELOAD_WINDOW_MS = 60000;
// 한 번으로 안 되는 경우가 있었다. 서비스 워커가 예전 껍데기를 들고 있으면 새로고침해도 같은
// 옛 청크 이름을 다시 받기 때문이다. 그래서 캐시를 비우고 한 번 더 준다. 두 번까지만이다.
const RELOAD_LIMIT = 2;

function reloadCount(now) {
  try {
    const [at, count] = String(window.sessionStorage.getItem(RELOAD_KEY) || '0:0').split(':').map(Number);
    return at > 0 && now - at < RELOAD_WINDOW_MS ? (count || 0) : 0;
  } catch {
    // 사생활 보호 모드. 기억할 수 없으면 새로고침을 반복할 수 있으므로 하지 않는다.
    return RELOAD_LIMIT;
  }
}

function markReloaded(now, count) {
  try { window.sessionStorage.setItem(RELOAD_KEY, `${now}:${count}`); } catch { /* 사생활 보호 모드 */ }
}

// 껍데기 캐시가 예전 index.html 을 들고 있으면 새로고침해도 같은 자리로 돌아온다. 지우고 나면
// 다음 요청은 반드시 네트워크를 탄다. 실패해도 그냥 새로고침은 해야 하므로 끝까지 막지 않는다.
async function dropShellCache() {
  try {
    const keys = await window.caches?.keys?.() ?? [];
    await Promise.all(keys.filter((key) => key.startsWith('doroparty-')).map((key) => window.caches.delete(key)));
  } catch { /* 캐시를 못 건드려도 새로고침은 시도한다 */ }
}

export function clearRouteReloadMark() {
  try { window.sessionStorage.removeItem(RELOAD_KEY); } catch { /* 사생활 보호 모드 */ }
}

export function lazyRoute(load) {
  return lazy(() => load().then((module) => {
    // 한 번 성공했으면 다음 배포 때 다시 새로고침할 수 있어야 한다.
    clearRouteReloadMark();
    return module;
  }).catch((error) => {
    const now = Date.now();
    const tried = reloadCount(now);
    if (tried >= RELOAD_LIMIT) throw error;
    markReloaded(now, tried + 1);
    // 두 번째부터는 캐시를 비우고 간다. 첫 번째는 대개 그냥 새로고침으로 낫는다.
    const ready = tried === 0 ? Promise.resolve() : dropShellCache();
    ready.then(() => window.location.reload());
    // 새로고침이 끝날 때까지는 아무것도 그리지 않는다. 거절을 그대로 올리면 그 사이에
    // 경계가 한 번 깜빡인다.
    return new Promise(() => {});
  }));
}

export class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="dp-route-error" role="alert">
        <div>
          <strong>화면을 불러오지 못했습니다.</strong>
          <p>앱이 업데이트된 뒤 예전 파일을 찾고 있을 수 있습니다. 다시 불러오면 해결됩니다.</p>
          <button
            type="button"
            onClick={() => { clearRouteReloadMark(); dropShellCache().then(() => window.location.reload()); }}
          >다시 불러오기</button>
        </div>
      </main>
    );
  }
}
